import type { AgentModelOption } from '@tabbio/types';
import { AGENT_MODEL_AUTO } from '@tabbio/utils/agent-models';

/**
 * The one-shot celebration when the user picks Tabbio's flagship model: a
 * rainbow sweep across the composer on web, one colourful beam cycle around
 * the composer on the phone.
 *
 * The pickers and the composers are not in the same tree (the phone's picker
 * sits in the chat header, the web phone layout's in its own header), so the
 * pick is announced on this small bus and every mounted composer plays it.
 */

export type CelebratedModel = Pick<AgentModelOption, 'id' | 'minimumTier' | 'locked'>;

type Listener = (model: CelebratedModel) => void;

const listeners = new Set<Listener>();

/**
 * Only an unlocked model on the Pro plan (the flagship tier) is celebrated.
 * Auto never is, a locked row opens the paywall instead of selecting, and
 * reduced motion turns the celebration off.
 */
export function shouldCelebrateModelPick(
  model: CelebratedModel,
  options: { reducedMotion: boolean },
): boolean {
  if (options.reducedMotion) return false;
  if (model.id === AGENT_MODEL_AUTO) return false;
  if (model.locked) return false;
  return model.minimumTier === 'pro';
}

/** Announces a pick; returns whether a celebration was played. */
export function celebrateModelPick(
  model: CelebratedModel,
  options: { reducedMotion: boolean },
): boolean {
  if (!shouldCelebrateModelPick(model, options)) return false;
  for (const listener of listeners) listener(model);
  return true;
}

export function subscribeComposerCelebration(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
